import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { ReportCardProps } from "../../interface/ReportCardProps";
import { ReportCardStatus } from "../../interface/ReportCardStatus";
import { ReportRunStatus } from "../../interface/ReportRunStatus";
import { StatusColors } from "../../utils/constants";

const chipColors: Record<ReportRunStatus, { bg: string; fg: string }> = {
  running: { bg: StatusColors.infoBg, fg: StatusColors.info },
  success: { bg: StatusColors.positiveBg, fg: StatusColors.positive },
  empty: { bg: StatusColors.warningBg, fg: StatusColors.warning },
  error: { bg: StatusColors.negativeBg, fg: StatusColors.negative },
};

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: theme.shape.borderRadius,
    "&:before": { display: "none" },
  },
  summary: {
    padding: theme.spacing(2, 3),
  },
  summaryContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    margin: 0,
  },
  icon: {
    color: theme.palette.primary.main,
    display: "flex",
    paddingTop: 2,
  },
  heading: {
    flex: 1,
    minWidth: 0,
  },
  // Descriptions run to one or two lines, which left the collapsed cards at two
  // different heights and the grid looking ragged. Reserving two lines for every one
  // of them squares the rows off, whatever the text does.
  description: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical" as const,
    WebkitLineClamp: 2,
    overflow: "hidden",
    minHeight: "2.9em",
  },
  details: {
    display: "block",
    padding: theme.spacing(0, 3, 3),
  },
  chip: {
    fontWeight: theme.typography.fontWeightBold as number,
    marginLeft: theme.spacing(1),
  },
}));

const LastRunChip = ({ lastRun }: { lastRun: ReportCardStatus }) => {
  const classes = useStyles();
  const colors = chipColors[lastRun.status];

  return (
    <Chip
      size="small"
      label={lastRun.label}
      className={classes.chip}
      style={{ backgroundColor: colors.bg, color: colors.fg }}
    />
  );
};

/**
 * One report on the Templates tab: collapsed to its name, what it produces and how its
 * last run ended, expanded to the form that runs it. Six reports open at once left the
 * page a wall of date pickers with no way to tell them apart at a glance.
 */
const ReportCard = ({
  title,
  description,
  icon,
  expanded,
  onToggle,
  lastRun,
  children,
}: ReportCardProps) => {
  const classes = useStyles();

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      elevation={3}
      className={classes.card}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        classes={{ root: classes.summary, content: classes.summaryContent }}
      >
        <span className={classes.icon}>{icon}</span>
        <span className={classes.heading}>
          <Typography variant="h6">
            {title}
            {lastRun && <LastRunChip lastRun={lastRun} />}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.description}
          >
            {description}
          </Typography>
        </span>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default ReportCard;
